import React, { useMemo } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import {
    Card,
    CardContent,
    Divider,
    Grid,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import { getGoogleMapsUrl, renderAddressMapLink } from './reportMapLinks';

const formatDate = (value) => {
    if (!value) return '--';
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('Do MMM YY hh:mm A') : value;
};

const formatNumber = (value, digits = 2) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return '--';
    return parsed.toLocaleString('en-IN', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

const SummaryItem = ({ label, value }) => (
    <Grid item xs={12} sm={6} md={3}>
        <Typography variant="body2" color="textSecondary">{label}</Typography>
        <Typography variant="body1" color="textPrimary"><b>{value}</b></Typography>
    </Grid>
);

const TravelPath = ({ reportData }) => {
    const dimensions = useSelector(liveTrackinglayoutSelector);
    const rows = useMemo(() => {
        return Array.isArray(reportData?.rows) ? reportData.rows : [];
    }, [reportData]);
    const timeFrom = reportData?.timeFrom || reportData?.reqBody?.timeFrom;
    const timeTo = reportData?.timeTo || reportData?.reqBody?.timeTo;
    const intervalMinutes = reportData?.intervalMinutes || reportData?.interval || '--';
    const distanceMode = reportData?.distanceMode === 'gps' ? 'GPS movement' : 'Odometer';
    const firstRow = rows[0];
    const lastRow = rows[rows.length - 1];

    const tableRows = useMemo(() => rows.map((row, index) => ({
        serial: row?.serial || index + 1,
        date: formatDate(row?.date),
        tripmeterReading: formatNumber(row?.tripmeterReading),
        lat: formatNumber(row?.lat, 6),
        lng: formatNumber(row?.lng, 6),
        location: renderAddressMapLink({
            text: row?.location || '--',
            lat: row?.lat,
            lng: row?.lng,
            source: row,
        }),
        mapUrl: row?.mapUrl || getGoogleMapsUrl(row?.lat, row?.lng),
    })), [rows]);

    if (!reportData) return null;

    return (
        <Paper style={{ ...dimensions.reportDetailViewBottom, overflow: 'auto' }}>
            <div style={{ margin: '10px' }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Card variant="outlined" style={{ backgroundColor: 'White' }}>
                            <CardContent>
                                <Typography variant="h6" style={{ color: 'RoyalBlue' }}>
                                    <b>Travel Path Summary</b>
                                </Typography>
                                <Divider style={{ marginBottom: '10px' }} />
                                <Grid container spacing={2}>
                                    <SummaryItem label="From" value={formatDate(timeFrom)} />
                                    <SummaryItem label="To" value={formatDate(timeTo)} />
                                    <SummaryItem label="Interval" value={`${intervalMinutes} Minutes`} />
                                    <SummaryItem label="Distance Mode" value={distanceMode} />
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" style={{ height: '100%', backgroundColor: 'White' }}>
                            <CardContent>
                                <Typography variant="subtitle1" style={{ color: 'Navy' }}><b>Start</b></Typography>
                                <Typography variant="body2" color="textSecondary">{firstRow ? formatDate(firstRow.date) : '--'}</Typography>
                                <Typography variant="body1" color="textPrimary">
                                    {renderAddressMapLink({
                                        text: firstRow?.location || '--',
                                        lat: firstRow?.lat,
                                        lng: firstRow?.lng,
                                        source: firstRow,
                                    })}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" style={{ height: '100%', backgroundColor: 'White' }}>
                            <CardContent>
                                <Typography variant="subtitle1" style={{ color: 'Navy' }}><b>End</b></Typography>
                                <Typography variant="body2" color="textSecondary">{lastRow ? formatDate(lastRow.date) : '--'}</Typography>
                                <Typography variant="body1" color="textPrimary">
                                    {renderAddressMapLink({
                                        text: lastRow?.location || '--',
                                        lat: lastRow?.lat,
                                        lng: lastRow?.lng,
                                        source: lastRow,
                                    })}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>#</b></TableCell>
                                        <TableCell><b>Date</b></TableCell>
                                        <TableCell align="right"><b>Tripmeter</b></TableCell>
                                        <TableCell align="right"><b>Latitude</b></TableCell>
                                        <TableCell align="right"><b>Longitude</b></TableCell>
                                        <TableCell><b>Location</b></TableCell>
                                        <TableCell><b>Map</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRows.map((row) => (
                                        <TableRow key={`${row.serial}-${row.date}-${row.lat}-${row.lng}`}>
                                            <TableCell>{row.serial}</TableCell>
                                            <TableCell>{row.date}</TableCell>
                                            <TableCell align="right">{row.tripmeterReading}</TableCell>
                                            <TableCell align="right">{row.lat}</TableCell>
                                            <TableCell align="right">{row.lng}</TableCell>
                                            <TableCell>{row.location}</TableCell>
                                            <TableCell>
                                                {row.mapUrl ? (
                                                    <Link href={row.mapUrl} target="_blank" rel="noreferrer">
                                                        Open
                                                    </Link>
                                                ) : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {tableRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Typography variant="body2" color="textSecondary">
                                                    No travel path rows found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </div>
        </Paper>
    );
};

export default TravelPath;
